/**
 * Copyright (c) 2019 Vendor Badge, LLC All Rights Reserved.
 * Created Date: Sunday, 17th February 2019
 *
 *  Portal - Login View: Registration Wizard
 */
import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { Link } from 'react-router-dom';
import queryString from 'query-string';
import { observer } from 'mobx-react';

// Material UI Components
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';

// Custom Components
import Wizard from 'components/Wizard';
import UserProfile from 'containers/pages/Login/sections/UserProfile';
import CompanyProfile from 'containers/pages/Login/sections/CompanyProfile';
import ReviewProfiles from 'containers/pages/Login/sections/ReviewProfiles';
import { useStores } from 'stores/RootStore';

/**
 * Registration Type (this.state.type)
 * Types: Vendor, Office
 */

const RegistrationWizard = props => {
  const { classes, theme } = props;
  const stores = useStores();
  const logo = () =>
    theme.palette.type === 'dark'
      ? require('assets/images/login-vb-shield-text-small-light.png')
      : require('assets/images/login-vb-shield-text-small.png');

  // Grab registration query params
  const params = queryString.parse(props.location.search);

  const { setField } = stores.registrationStore;

  if (params.type !== undefined) {
    setField('type', params.type);
    stores.appStore.debug(`Login Registration [component] query params`, params);
    setField('invite', params.type === 'invite');

    if (params.id !== undefined) {
      setField('inviteId', params.id);
    }

    setField('isLoading', false);
  }

  const profileStep = {
    stepName: 'Profile',
    stepComponent: UserProfile,
    stepId: 'profile',
    stepSubtitle: "Let's get to know each other...",
  };

  const companyStep = {
    stepName: 'Company',
    stepComponent: CompanyProfile,
    stepId: 'company',
    stepSubtitle: 'Tell us about your company...',
  };

  const reviewStep = {
    stepName: 'Review',
    stepComponent: ReviewProfiles,
    stepId: 'review',
    stepSubtitle: 'Did we get it right?',
  };

  var steps = [];

  // Step 1
  steps.push(profileStep);

  // Step 2 only if type not equal to invite
  if (params.type !== 'invite') {
    steps.push(companyStep);
  }

  // Step 3 or 2 if type equal to invite
  steps.push(reviewStep);

  stores.appStore.debug('Login RegistrationWizard [component] Rendered successfully.');
  return (
    <Grid container justify='center' className={classes.wrapper}>
      <Grid item sm={11} xs={12}>
        <Wizard validate steps={steps} logo={logo} title='' subtitle='' previousButtonText='Back' />
      </Grid>
      <Grid item xs={11} className={classes.wizardFooter}>
        <Typography className={classes.requiredFields}>*Required Fields</Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container justify='center' alignItems='center' className={classes.wizardSignin}>
          <Grid item xs={12}>
            <Typography>Already have an account?</Typography>
          </Grid>
          <Grid item xs={12}>
            <Button component={Link} to='/login' color='primary' className={classes.signin}>
              Sign In
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

RegistrationWizard.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
};

const styles = theme => ({
  wrapper: {},
  wizardFooter: {
    marginTop: 15,
  },
  requiredFields: {
    fontSize: '10pt',
    color: theme.palette.error.main,
  },
  signin: {
    marginTop: -6,
    padding: 3,
  },
  wizardSignin: {
    marginTop: -45,
    textAlign: 'center',
    [theme.breakpoints.down('xs')]: {
      marginTop: 10,
    },
  },
});

export default withStyles(styles, { withTheme: true })(observer(RegistrationWizard));
